import * as THREE from 'three';
import type { Handedness, HandGestureTracker } from '@/xr/handInput';

/**
 * Which input device the player is actually using — the signal that drives the
 * MR range's automatic mode selection: hands → launch-monitor mode (they're
 * gripping a real club), club-hand controller held → virtual-swing mode.
 *
 * The hard part is that PRESENCE PROVES NOTHING. Quest multimodal input lists
 * tracked hands and controllers as input sources simultaneously (the reason
 * XRShotControls binds four slots), and a controller resting on a table keeps
 * a perfectly valid grip pose. "Held" therefore means evidence of a hand on
 * the device:
 *
 *  1. Gamepad interaction — any button pressed or capacitively TOUCHED (Quest
 *     Touch reports thumb/finger rest continuously while held), analog trigger
 *     or squeeze past a whisker, thumbstick deflection. A resting controller
 *     produces none of these.
 *  2. Pose motion — the positional EXTENT of the grip over a short window
 *     (max sample distance from the window mean, deliberately not integrated
 *     path length, which accumulates tracking noise into false motion). A
 *     controller in a human hand at "rest" still drifts centimetres; a
 *     controller on a table jitters millimetres.
 *
 * Latching keeps the result calm: sustained activity latches "held" in well
 * under a second (a real button press latches instantly — nothing on a table
 * presses its own trigger), and only a long idle spell unlatches it, so
 * setting the controller down between shots is a non-event.
 *
 * The recommendation is deliberately three-valued: 'controller', 'hands', or
 * undefined — ABSENCE OF EVIDENCE IS NOT EVIDENCE. Hand-tracking loss, a
 * doffed headset, or a dead controller with no hands visible all produce
 * undefined, and the app changes nothing.
 *
 * Scoped to the CLUB-HAND controller only: the swing input reads exactly that
 * controller, and the off-hand controller remains monitor mode's documented
 * pointer fallback, so it must not force swing mode.
 */

export type InputModality = 'controller' | 'hands';

export type InputModalityDetectorOptions = {
  /** Which controller carries the club — the YEZRO mounts on the right. */
  clubHand?: Handedness;
};

/** Analog whiskers: above these, someone's finger is on the control. */
const TRIGGER_TOUCH_VALUE = 0.05;
const STICK_TOUCH_VALUE = 0.15;
/** Pose-motion window and thresholds (see class doc). */
const MOTION_WINDOW_S = 0.75;
const MOTION_EPS = 0.018;
const MOTION_ANGLE_DEG = 10;
/** Activity decays at this fraction of real time when the signal drops out —
 *  motion evidence flickers as the window slides, and demanding an unbroken
 *  run would keep resetting the latch for a genuinely held controller. */
const ACTIVITY_DECAY = 0.5;
/** Sustained activity before "held" latches (a press latches instantly). */
const ENTER_HELD_S = 0.6;
/** Idle this long before "held" unlatches — protects set-it-down pauses. */
const EXIT_IDLE_S = 6;
/** Hands must be continuously tracked this long to earn a recommendation. */
const HANDS_CONFIRM_S = 1.0;
/** Ring size: 64 frames ≈ 0.7-0.9 s at 72-90 Hz — spans the motion window. */
const SAMPLE_CAPACITY = 64;

type PoseSample = { position: THREE.Vector3; quaternion: THREE.Quaternion; time: number };

export class InputModalityDetector {
  #clubHand: Handedness;

  #elapsed = 0;
  #held = false;
  #activityFor = 0;
  #idleFor = Infinity;
  #handsTrackedFor = 0;
  #samples: PoseSample[] = [];

  // scratch
  #mean = new THREE.Vector3();

  constructor(options: InputModalityDetectorOptions = {}) {
    this.#clubHand = options.clubHand ?? 'right';
  }

  /** The latched "club controller is in a hand" heuristic. */
  get clubControllerHeld(): boolean {
    return this.#held;
  }

  /** What the input evidence supports right now; undefined = no opinion. */
  get recommendation(): InputModality | undefined {
    if (this.#held) return 'controller';
    if (this.#idleFor >= EXIT_IDLE_S && this.#handsTrackedFor >= HANDS_CONFIRM_S) return 'hands';
    return undefined;
  }

  /** Session end: drop every latch and timer. */
  reset() {
    this.#held = false;
    this.#activityFor = 0;
    this.#idleFor = Infinity;
    this.#handsTrackedFor = 0;
    this.#samples.length = 0;
  }

  update(
    dt: number,
    frame: XRFrame,
    referenceSpace: XRReferenceSpace,
    inputSources: readonly (XRInputSource | undefined)[],
    hands: { left: HandGestureTracker; right: HandGestureTracker },
  ) {
    this.#elapsed += dt;

    // Hands-confirm clock: any tracked hand keeps it running
    if (hands.left.tracked || hands.right.tracked) {
      this.#handsTrackedFor += dt;
    } else {
      this.#handsTrackedFor = 0;
    }

    const source = inputSources.find(
      candidate => !!candidate && !(candidate as any).hand && candidate.handedness === this.#clubHand
    );
    if (!source) {
      // Disconnected (battery death, sleep): no activity — the idle clock runs
      this.#samples.length = 0;
      this.#tickActivity(dt, false, false);
      return;
    }

    // Signal family 1: gamepad interaction
    let pressed = false;
    let touched = false;
    const gamepad = source.gamepad;
    if (gamepad) {
      for (const button of gamepad.buttons) {
        if (button.pressed) pressed = true;
        if (button.touched) touched = true;
      }
      if ((gamepad.buttons[0]?.value ?? 0) > TRIGGER_TOUCH_VALUE) touched = true;
      if ((gamepad.buttons[1]?.value ?? 0) > TRIGGER_TOUCH_VALUE) touched = true;
      if (Math.abs(gamepad.axes[2] ?? 0) > STICK_TOUCH_VALUE) touched = true;
      if (Math.abs(gamepad.axes[3] ?? 0) > STICK_TOUCH_VALUE) touched = true;
    }

    // Signal family 2: pose-motion extent over the window
    const gripSpace = source.gripSpace;
    const pose = gripSpace ? frame.getPose(gripSpace, referenceSpace) : null;
    let moving = false;
    if (pose) {
      const p = pose.transform.position;
      const o = pose.transform.orientation;
      this.#samples.push({
        position: new THREE.Vector3(p.x, p.y, p.z),
        quaternion: new THREE.Quaternion(o.x, o.y, o.z, o.w),
        time: this.#elapsed,
      });
      if (this.#samples.length > SAMPLE_CAPACITY) this.#samples.shift();
      while (this.#samples.length && this.#elapsed - this.#samples[0].time > MOTION_WINDOW_S) {
        this.#samples.shift();
      }

      if (this.#samples.length >= 8) {
        this.#mean.set(0, 0, 0);
        for (const sample of this.#samples) this.#mean.add(sample.position);
        this.#mean.divideScalar(this.#samples.length);
        // |dot| < cos(θ/2) ⇔ the two orientations differ by more than θ
        const angleCos = Math.cos(THREE.MathUtils.degToRad(MOTION_ANGLE_DEG / 2));
        const newest = this.#samples[this.#samples.length - 1];
        for (const sample of this.#samples) {
          if (sample.position.distanceTo(this.#mean) > MOTION_EPS) { moving = true; break; }
          if (Math.abs(sample.quaternion.dot(newest.quaternion)) < angleCos) { moving = true; break; }
        }
      }
    } else {
      this.#samples.length = 0;
    }

    this.#tickActivity(dt, pressed, touched || moving);
  }

  #tickActivity(dt: number, pressed: boolean, active: boolean) {
    if (pressed) {
      // A real press is unambiguous — nothing on a table pulls its own trigger
      this.#held = true;
      this.#activityFor = ENTER_HELD_S;
      this.#idleFor = 0;
      return;
    }
    if (active) {
      this.#activityFor += dt;
      this.#idleFor = 0;
      if (this.#activityFor >= ENTER_HELD_S) this.#held = true;
    } else {
      // Leaky, not reset: the motion signal flickers as the sliding window's
      // contents change, and a hard reset would keep a genuinely held (but
      // capacitively silent) controller from ever latching. A brief table
      // bump still decays away long before it reaches the latch.
      this.#activityFor = Math.max(0, this.#activityFor - dt * ACTIVITY_DECAY);
      this.#idleFor = Math.min(this.#idleFor + dt, Number.MAX_SAFE_INTEGER);
      if (this.#idleFor >= EXIT_IDLE_S) this.#held = false;
    }
  }
}
